import React, { useState, useRef, useEffect } from "react";
import { ChromePicker } from "react-color";
import styles from "./CompactColorPicker.module.css";

/**
 * Compact color picker — small swatch + hex input inline, full ChromePicker
 * opens on click in a popover. Used by TemplateEditorPage and VisualEditor.
 */
function CompactColorPicker({ color, onChange }) {
    const [open, setOpen] = useState(false);
    const [hexInput, setHexInput] = useState(color);
    const wrapRef = useRef(null);

    useEffect(() => {
        setHexInput(color);
    }, [color]);

    useEffect(() => {
        if (!open) return;
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    function toCss(c) {
        if (!c) return "";
        if (typeof c === "string") return c;
        const { r, g, b, a } = c.rgb || {};
        if (a != null && a < 1) return `rgba(${r}, ${g}, ${b}, ${a})`;
        return c.hex || `rgb(${r}, ${g}, ${b})`;
    }

    function handlePickerChange(c) {
        const css = toCss(c);
        onChange(css);
    }

    function handleHexBlur() {
        const trimmed = hexInput.trim();
        if (trimmed && trimmed !== color) onChange(trimmed);
    }

    function handleHexKey(e) {
        if (e.key === "Enter") {
            e.preventDefault();
            handleHexBlur();
        }
    }

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={styles.swatch}
                style={{ background: color }}
                onClick={() => setOpen(!open)}
                aria-label="Open color picker"
            />
            <input
                type="text"
                className={styles.hex}
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value)}
                onBlur={handleHexBlur}
                onKeyDown={handleHexKey}
                spellCheck={false}
            />
            {open && (
                <div className={styles.popover}>
                    <ChromePicker
                        color={color}
                        onChange={handlePickerChange}
                        disableAlpha={false}
                    />
                </div>
            )}
        </div>
    );
}

export default CompactColorPicker;
